





































































































































































import {
  defineComponent,
  useRouter,
  useContext,
  ref,
  useFetch,
  computed,
} from "@nuxtjs/composition-api";
import NotificationModal from "~/components/NotificationModal.vue";
import { useAddresses } from "~/modules/customer/composables/useAddresses";
import userAddressesGetters from "~/modules/customer/getters/userAddressesGetters";
import UserAddressDetails from "~/components/UserAddressDetails.vue";
import { CustomerAddress } from "~/modules/GraphQL/types";
import SkeletonLoader from "~/components/SkeletonLoader/index.vue";
import { useCountrySearch } from "~/composables/useCountrySearch";
import type { Country } from "~/modules/GraphQL/types";
import AddressEditModal from "./AddressEditModal.vue";
import AddressNewModal from "./AddressNewModal.vue";
import { useRegionStore } from "~/stores/extensionApi/region";
import { useUiNotification } from "~/composables";
import type { UiNotificationType } from "~/composables/useUiNotification";
import Pagination from "~/modules/customer/pages/MyAccount/Pagination.vue";
import { useUiHelpers } from "~/composables/useUiHelpers";

export default defineComponent({
  name: "AddressesDetails",
  components: {
    UserAddressDetails,
    SkeletonLoader,
    AddressEditModal,
    AddressNewModal,
    NotificationModal,
    Pagination,
  },
  setup() {
    const context = useContext();
    const router = useRouter();

    const userAddresses = ref<CustomerAddress[]>([]);
    const countries = ref<Country[]>([]);
    const addressesWithCountryName = ref([]);
    const { send: sendNotification } = useUiNotification();
    const isRemoveConfirmVisible = ref(false);
    const addressToRemove = ref<CustomerAddress>(null);
     const { changePage } = useUiHelpers();

    const itemsPerPage = 10; 
    const totalPages = ref(1);
    const currentPage = ref(1);

    const paginatedAddresses = computed(() => {
      const startIndex = (currentPage.value - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      return addressesWithCountryName.value.slice(startIndex, endIndex);
    });

    const goToPage = (page: number) => {
      currentPage.value = page;
      changePage(page, false);
    };

    const {
      load,
      remove,
      update,
      loading,
      save,
      error: errorAddresses,
    } = useAddresses();
    const { load: loadCountries } = useCountrySearch();
    const regionStore = useRegionStore();

    const { fetch } = useFetch(async () => {
      const [addressesResponse, countriesResponse] = await Promise.all([
        load(),
        loadCountries(),
      ]);
      userAddresses.value =
        userAddressesGetters.getAddresses(addressesResponse);
      countries.value = countriesResponse;

      addressesWithCountryName.value = userAddresses.value
        .map((address) => ({
          ...address,
          countryName:
            countries.value.find(({ id }) => id === address.country_code)
              ?.full_name_locale ?? address.country_code,
        }))
        .sort((a, b) => b.id - a.id);

      totalPages.value = Math.ceil(
        addressesWithCountryName.value.length / itemsPerPage
      );

      let routerQuery;
      if (process?.client) {
        routerQuery = router?.resolve(
          (window?.location?.pathname + window?.location?.search)?.slice(1)
        )?.route?.query;
      } else {
        routerQuery = router?.currentRoute?.query;
      }

      currentPage.value = parseInt(routerQuery?.page) || 1;

      // Fetch states data if not already available
      if (!regionStore.getStateData("VN")) {
        await regionStore.fetchStateData("VN");
      }
      states.value = regionStore.getStateData("VN");
    });

    const goToCreateAddressPage = () =>
      router.push(
        context.localeRoute({ name: "customer-addresses-details-new" })
      );

    const removeAddress = async (address: CustomerAddress) => {
      addressToRemove.value = address;
      isRemoveConfirmVisible.value = true;
      closeDropdown();
    };

    const confirmRemove = async () => {
      if (addressToRemove.value) {
        try {
          isRemoveConfirmVisible.value = false;
          await remove({ address: addressToRemove.value });
          closeDropdown();
          fetch();
        } catch (error) {
          console.error("Error removing item:", error);
        } finally {
          addressToRemove.value = null;
        }
      }
    };

    const cancelRemove = () => {
      isRemoveConfirmVisible.value = false;
      addressToRemove.value = null;
    };

    const editAddress = ref(false);
    const newAddress = ref(false);
    const editedAddress = ref({});
    const states = ref([]);
    const cities = ref([]);
    const wards = ref([]);
    const checkLengDetail = ref(false);
    const resetNewAddressForm = ref(false);
    const openedDropdown = ref(null);

    const openEditAddressModal = async (address) => {
      editedAddress.value = { ...address };
      editAddress.value = true;
      closeDropdown();

      // Fetch cities and wards data if not already available
      if (!regionStore.getCityData(address?.region?.region_id)) {
        await regionStore.fetchCityData(address?.region?.region_id);
      }
      cities.value = regionStore.getCityData(address?.region?.region_id);

      const addressCityId = getCityIdByName(address.city);
      if (!regionStore.getWardData(addressCityId)) {
        await regionStore.fetchWardData(addressCityId);
      }
      wards.value = regionStore.getWardData(addressCityId);
    };

    const openNewAddressModal = async () => {
      newAddress.value = true;
    };

    const getCityIdByName = (name: string): number => {
      const cityObject = cities.value.find((city) => city?.cityname === name);
      return cityObject ? cityObject.city_id : 0;
    };

    const closeEditModal = () => {
      editAddress.value = false;
    };

    const closeNewModal = () => {
      newAddress.value = false;
    };

    const notify = (type: UiNotificationType, message: string) => {
      sendNotification({
        id: Symbol(type),
        message,
        type,
        icon: type === "success" ? "check" : "cross",
        persist: false,
        title: "Sổ địa chỉ",
      });
    };

    const updateAddress = async (updatedAddress: CustomerAddress) => {
      updatedAddress.custom_attributes = updatedAddress.custom_attributes?.map(
        (attr) => {
          const { __typename, ...rest } = attr as any;
          return rest;
        }
      );
      await update({ address: updatedAddress });
      if (errorAddresses.value && errorAddresses?.value?.update?.message) {
        notify("danger", "Lỗi khi cập nhật địa chỉ!");
      } else {
        notify("success", "Cập nhật thành công!");
        fetch();
        closeEditModal();
      }
    };

    const createAddress = async (newAddressData: CustomerAddress) => {
      await save({ address: newAddressData });
      if (errorAddresses.value && errorAddresses?.value?.save?.message) {
        notify("danger", "Lỗi khi thêm địa chỉ!");
      } else {
        notify("success", "Thêm địa chỉ thành công!");
        fetch();
        resetNewAddressForm.value = true;
        closeNewModal();
      }
    };

    const onChangeState = async (regionId: number) => {
      await regionStore.fetchCityData(regionId);
      cities.value = regionStore.getCityData(regionId);
    };

    const onChangeCity = async (cityId: number) => {
      await regionStore.fetchWardData(cityId);
      wards.value = regionStore.getWardData(cityId);
    };

    const updateDefaultAddress = async (
      address: CustomerAddress,
      index: number
    ) => {
      const updatedAddress = {
        ...address,
        default_shipping: true,
        default_billing: true,
        custom_attributes: address.custom_attributes?.map((attr) => {
          const { __typename, ...rest } = attr as any;
          return rest;
        }),
      };
      delete updatedAddress.countryName;
      await update({ address: updatedAddress });
      fetch();
      closeDropdown();
    };

    const handleResetForm = (value: boolean) => {
      resetNewAddressForm.value = value;
    };

    const toggleDropdown = (addressId) => {
      if (openedDropdown.value === addressId) {
        openedDropdown.value = null;
      } else {
        openedDropdown.value = addressId;
      }
    };

    const closeDropdown = () => {
      openedDropdown.value = null;
    };

    return {
      countries,
      userAddresses,
      addressesWithCountryName,
      paginatedAddresses, 
      goToCreateAddressPage,
      removeAddress,
      getId: userAddressesGetters.getId,
      isDefault: userAddressesGetters.isDefaultShipping,
      editAddress,
      newAddress,
      editedAddress,
      states,
      cities,
      wards,
      loading,
      checkLengDetail,
      openEditAddressModal,
      openNewAddressModal,
      closeEditModal,
      closeNewModal,
      updateAddress,
      createAddress,
      onChangeState,
      onChangeCity,
      updateDefaultAddress,
      errorAddresses,
      resetNewAddressForm,
      handleResetForm,
      openedDropdown,
      toggleDropdown,
      closeDropdown,
      confirmRemove,
      isRemoveConfirmVisible,
      cancelRemove,
      goToPage,
      totalPages,
      currentPage,
    };
  },
});
